import { useEffect, useMemo, useState } from 'react';
import PWAInstallModal from '@/components/PWAInstallModal';
import usePWAInstall from '@/hooks/usePWAInstall';
import useAppSettingsStore from '@/store/appSettingsStore';
import { buildPwaLogoUrl, mergeInstallSettings } from '@/utils/pwa';

/**
 * Production PWA install entry-point.
 * The heavy install state management lives in `usePWAInstall` so the UI can be
 * reused elsewhere if we later want an install banner or settings action.
 */
export default function PWAInstallPrompt() {
  const { settings, fetchSettings } = useAppSettingsStore();
  const {
    canInstall,
    dismiss,
    hasPromptEvent,
    isBrowserSupported,
    isIOS,
    isInstalled,
    promptInstall,
  } = usePWAInstall();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!settings.app_name) {
      void fetchSettings();
    }
  }, [fetchSettings, settings.app_name]);

  useEffect(() => {
    if (canInstall) {
      setIsOpen(true);
      return;
    }

    if (isInstalled) {
      setIsOpen(false);
    }
  }, [canInstall, isInstalled]);

  const installSettings = useMemo(() => {
    return mergeInstallSettings(settings);
  }, [settings]);

  const displayName = installSettings.app_name;
  const logoUrl = installSettings.app_logo || buildPwaLogoUrl();

  const handleClose = () => {
    dismiss();
    setIsOpen(false);
  };

  const handleInstall = async () => {
    const result = await promptInstall();

    if (result?.outcome === 'accepted' || result?.outcome === 'ios-manual') {
      setIsOpen(false);
      return;
    }
  };

  if (isInstalled || (!canInstall && !isOpen) || (!isBrowserSupported && !isIOS)) {
    return null;
  }

  return (
    <PWAInstallModal
      appName={displayName}
      iconUrl={logoUrl}
      isIOS={isIOS}
      isOpen={isOpen}
      isPromptAvailable={hasPromptEvent}
      onClose={handleClose}
      onInstall={handleInstall}
    />
  );
}
